import React, { useState, useEffect } from 'react';
import { X, Trash2, AlertCircle, Calendar, Info } from 'lucide-react';
import {
  annualPlanningService,
  AcademicEvent,
  EventCategory,
  EventVisibility,
  CreateAcademicEventDto,
  UpdateAcademicEventDto,
  VISIBILITY_LABELS,
} from '../../services/annualPlanningService';

interface EventFormNewProps {
  event: AcademicEvent | null;
  categories: EventCategory[];
  academicYearId: string;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
  initialDate?: Date;
  initialEndDate?: Date;
  /** View-only mode for users without CALENDAR_ANNUAL_MANAGE */
  readOnly?: boolean;
}

interface FormData {
  categoryId: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  isAllDay: boolean;
  color: string;
  visibility: EventVisibility | '';
  campusId: string;
}

// Helper to format date as YYYY-MM-DD without timezone issues
const formatDateToLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getInitialFormData = (
  event: AcademicEvent | null,
  categories: EventCategory[],
  initialDate?: Date,
  initialEndDate?: Date
): FormData => {
  if (event) {
    const category = categories.find(c => c.id === event.categoryId);
    return {
      categoryId: event.categoryId || categories[0]?.id || '',
      title: event.title,
      description: event.description || '',
      startDate: event.startDate.split('T')[0],
      endDate: event.endDate.split('T')[0],
      isAllDay: event.isAllDay ?? true,
      // A colour identical to the category's is not a custom override
      color: event.color && event.color !== category?.color ? event.color : '',
      // Same for visibility: matching the category means "inherited"
      visibility: event.visibility && event.visibility !== category?.visibility ? event.visibility : '',
      campusId: event.campusId || '',
    };
  }

  const today = initialDate || new Date();
  const endDay = initialEndDate || today;
  // Use local date formatting to avoid timezone issues
  const startDateStr = formatDateToLocal(today);
  const endDateStr = formatDateToLocal(endDay);

  return {
    categoryId: categories[0]?.id || '',
    title: '',
    description: '',
    startDate: startDateStr,
    endDate: endDateStr,
    isAllDay: true,
    color: '',
    visibility: '',
    campusId: '',
  };
};

export const EventFormNew: React.FC<EventFormNewProps> = ({
  event,
  categories,
  academicYearId,
  onClose,
  onSaved,
  onDeleted,
  initialDate,
  initialEndDate,
  readOnly = false,
}) => {
  const isEditing = !!event;
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for end date stored by drag selection (fallback for multi-day events)
  const storedEndDate = (window as unknown as { __calendarEndDate?: Date }).__calendarEndDate;
  const effectiveEndDate = initialEndDate || storedEndDate;

  const [formData, setFormData] = useState<FormData>(() =>
    getInitialFormData(event, categories, initialDate, effectiveEndDate)
  );

  // Clean up the stored end date on mount
  React.useEffect(() => {
    return () => {
      delete (window as unknown as { __calendarEndDate?: Date }).__calendarEndDate;
    };
  }, []);

  // Get selected category
  const selectedCategory = categories.find(c => c.id === formData.categoryId);

  // Update form when category changes
  useEffect(() => {
    if (selectedCategory && !formData.color) {
      // Don't override custom color
    }
  }, [selectedCategory, formData.color]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    setError(null);

    if (!formData.categoryId) {
      setError('Veuillez sélectionner une catégorie');
      return;
    }

    if (!formData.title.trim()) {
      setError('Le nom de l\'événement est requis');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      setError('Les dates de début et de fin sont requises');
      return;
    }

    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      setError('La date de fin ne peut pas être antérieure à la date de début');
      return;
    }

    try {
      setLoading(true);

      // Stored type derived from the category (the form has no type selector)
      const eventType: 'VACATION' | 'OTHER' = selectedCategory?.code === 'VACATION' ? 'VACATION' : 'OTHER';

      if (isEditing) {
        const categoryChanged = formData.categoryId !== (event.categoryId || '');
        const data: UpdateAcademicEventDto = {
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          startDate: formData.startDate,
          endDate: formData.endDate,
          isAllDay: formData.isAllDay,
          color: formData.color || null,
          visibility: formData.visibility || null,
        };
        if (categoryChanged) {
          data.categoryId = formData.categoryId;
          // Only re-derive the type when the category moves, so auto-synced
          // holidays / year markers keep their HOLIDAY / RENTREE type on a
          // plain rename or date change and are not re-created by the sync
          data.eventType = eventType;
        }
        await annualPlanningService.updateEvent(event.id, data);
      } else {
        const data: CreateAcademicEventDto = {
          academicYearId,
          categoryId: formData.categoryId,
          title: formData.title.trim(),
          description: formData.description.trim() || undefined,
          eventType,
          startDate: formData.startDate,
          endDate: formData.endDate,
          isAllDay: formData.isAllDay,
          color: formData.color || undefined,
          visibility: formData.visibility || undefined,
          campusId: formData.campusId || undefined,
        };
        await annualPlanningService.createEvent(data);
      }

      onSaved();
    } catch (err: unknown) {
      console.error('Failed to save event:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Une erreur est survenue');
      } else {
        setError('Une erreur est survenue');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (readOnly) return;
    if (!event || !confirm('Êtes-vous sûr de vouloir supprimer cet événement?')) return;

    try {
      setDeleting(true);
      await annualPlanningService.deleteEvent(event.id);
      onDeleted();
    } catch (err) {
      console.error('Failed to delete event:', err);
      setError('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setFormData(prev => {
      const previousCategory = categories.find(c => c.id === prev.categoryId);
      return {
        ...prev,
        categoryId,
        // Reset visibility override when category changes
        visibility: '',
        // Drop a colour that merely mirrored the old category, otherwise the
        // event keeps showing in the previous category's colour
        color: prev.color && prev.color !== previousCategory?.color ? prev.color : '',
      };
    });
  };

  const effectiveColor = formData.color || selectedCategory?.color || '#3B82F6';
  const effectiveVisibility = formData.visibility || selectedCategory?.visibility || 'ALL';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${effectiveColor}20` }}
            >
              <Calendar size={20} style={{ color: effectiveColor }} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {readOnly ? 'Détails de l\'événement' : isEditing ? 'Modifier l\'événement' : 'Nouvel événement'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* fieldset natively disables every input/button inside in read-only mode */}
          <fieldset disabled={readOnly} className="space-y-4 border-0 p-0 m-0 min-w-0">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Catégorie *
            </label>
            <div className="grid grid-cols-2 gap-2">
              {categories.filter(c => c.isActive).map(category => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => handleCategoryChange(category.id)}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all
                    ${formData.categoryId === category.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: category.color }}
                  />
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 text-sm truncate">
                      {category.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {VISIBILITY_LABELS[category.visibility]}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {categories.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">
                Aucune catégorie disponible. Créez-en une dans les paramètres.
              </p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de l'événement *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="ex: Rentrée des classes"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de début *
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin *
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                min={formData.startDate}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </div>

          {/* All Day Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isAllDay"
              checked={formData.isAllDay}
              onChange={(e) => setFormData({ ...formData, isAllDay: e.target.checked })}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="isAllDay" className="text-sm text-gray-700">
              Événement sur toute la journée
            </label>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optionnel)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Ajoutez des détails sur cet événement..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Advanced Options (Color Override) */}
          <details className="group">
            <summary className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900">
              <span>Options avancées</span>
            </summary>
            <div className="mt-4 space-y-4 pl-4 border-l-2 border-gray-200">
              {/* Color Override */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Couleur personnalisée
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={effectiveColor}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <div
                    className="flex-1 h-10 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                    style={{ backgroundColor: effectiveColor }}
                  >
                    Aperçu
                  </div>
                  {formData.color && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, color: '' })}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Réinitialiser
                    </button>
                  )}
                </div>
              </div>

              {/* Visibility Override */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visibilité (par défaut: {VISIBILITY_LABELS[selectedCategory?.visibility || 'ALL']})
                </label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value as EventVisibility | '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Utiliser la visibilité de la catégorie</option>
                  {Object.entries(VISIBILITY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </details>

          {/* Visibility Info */}
          {selectedCategory && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <span className="font-medium">Visibilité:</span>{' '}
                {VISIBILITY_LABELS[effectiveVisibility]}
                {formData.visibility && (
                  <span className="text-blue-500"> (personnalisée)</span>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
          </fieldset>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
            <div>
              {isEditing && !readOnly && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Trash2 size={18} />
                  Supprimer
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {readOnly ? 'Fermer' : 'Annuler'}
              </button>
              {!readOnly && (
                <button
                  type="submit"
                  disabled={loading || !formData.categoryId}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Enregistrement...' : isEditing ? 'Mettre à jour' : 'Créer'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
